import { Fragment, createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { FALLBACK_LOCALE, SUPPORTED_LOCALES, matchLocale, resolveLocale } from "@sss/shared";
import type { Locale } from "@sss/shared";
import { DEFAULT_LOCALE } from "../lib/config";
import { LOCALE_NAMES, MESSAGES } from "./messages";
import type { Messages } from "./messages";
import { formatBytes, formatUtcTime, interpolate, selectPlural } from "./format";
import type { PluralForms } from "./types";

export type { Messages } from "./messages";
export type { PluralForms } from "./types";
export { LOCALE_NAMES, MESSAGES } from "./messages";
export { SUPPORTED_LOCALES } from "@sss/shared";
export type { Locale } from "@sss/shared";

/**
 * Where a deliberate choice is remembered. Per-browser and per-origin, like
 * any other reading preference — the server is never told, and nothing about
 * the choice travels with a share link.
 */
const STORAGE_KEY = "sss.locale";

function storedLocale(): Locale | undefined {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? matchLocale(stored) : undefined;
  } catch {
    // Private mode, blocked site data, an embedded context — a remembered
    // language is a convenience, not something worth throwing over.
    return undefined;
  }
}

function rememberLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // See above: the choice still applies for this page's lifetime.
  }
}

/**
 * The language to start in, most specific signal first: a choice made here
 * before, then the browser's own ordered preference list, then the
 * operator's DEFAULT_LOCALE, then English.
 */
export function initialLocale(): Locale {
  return resolveLocale(storedLocale(), ...(navigator.languages ?? [navigator.language]), DEFAULT_LOCALE);
}

export interface I18n {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** The active locale's whole message tree — read it, don't look strings up by key. */
  m: Messages;
  /** Interpolates `{name}` placeholders into a message. */
  t: (template: string, values?: Record<string, string | number>) => string;
  /** Picks the right plural form for `count`, then interpolates. */
  tp: (forms: PluralForms, count: number, values?: Record<string, string | number>) => string;
  /**
   * Interpolates where one or more values are React elements — a `<code>`,
   * a clickable word — and returns nodes rather than a string. Keeps markup
   * out of the message files without splitting a sentence into fragments a
   * translator can't reorder.
   */
  tx: (template: string, values: Record<string, ReactNode>) => ReactNode[];
  formatBytes: (bytes: number) => string;
  formatUtcTime: (timestamp: number) => string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    // Assistive technology and the browser's own hyphenation, font pick and
    // translation offer all key off this.
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    rememberLocale(next);
    setLocaleState(next);
  }, []);

  const value = useMemo<I18n>(() => {
    const m = MESSAGES[locale] ?? MESSAGES[FALLBACK_LOCALE];
    return {
      locale,
      setLocale,
      m,
      t: (template, values) => interpolate(template, values),
      tp: (forms, count, values) => selectPlural(locale, forms, count, values),
      tx: (template, values) => interpolateNodes(template, values),
      formatBytes: (bytes) => formatBytes(bytes, locale, m.bytes),
      formatUtcTime: (timestamp) => formatUtcTime(timestamp, locale),
    };
  }, [locale, setLocale]);

  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n(): I18n {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside <I18nProvider>");
  return context;
}

/**
 * The node-returning half of interpolation. Splits on `{name}` and swaps in
 * the matching node, leaving an unknown placeholder as literal text so a
 * typo shows up on the page rather than vanishing from the sentence.
 */
function interpolateNodes(template: string, values: Record<string, ReactNode>): ReactNode[] {
  return template.split(/(\{\w+\})/g).map((part, index) => {
    const name = /^\{(\w+)\}$/.exec(part)?.[1];
    if (name && name in values) {
      return createElement(Fragment, { key: index }, values[name]);
    }
    return part;
  });
}
